import React, { useState, useEffect } from 'react';
import { HelpCircle, Database, BarChart2, GitBranch, Zap, FileText, AlertCircle, CheckCircle, Play, Settings, Pause, Clock, Activity, RefreshCw } from 'react-feather';

const SuggestedQuestions = ({ onQuestionClick, isVisible = true, messages = [], isDarkMode = false, mode = 'default' }) => {
  const [currentSet, setCurrentSet] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Define question pools tailored to current AI tool capacity
  const questionPools = [
    // Pool 1: Troubleshooting & Logs  
    [
      {
        id: 7,
        text: "Why did my latest DAG run fail?",
        icon: <AlertCircle size={14} color="#438593" />,
        category: "troubleshooting"
      },
      {
        id: 12,
        text: "Show me my recent pipeline failures",
        icon: <HelpCircle size={14} color="#438593" />,
        category: "analysis"
      },
    ],

    // Pool 2: DAG Status & Monitoring
    [
      {
        id: 1,
        text: "Show me all my DAGs and schedules",
        icon: <BarChart2 size={14} color="#438593" />,
        category: "monitoring"
      },
      {
        id: 2,
        text: "When is my next DAG scheduled to run?",
        icon: <Clock size={14} color="#438593" />,
        category: "scheduling"
      },
    ],

    // Pool 3: Historical Data & Analysis
    [
      {
        id: 10,
        text: "Graph me the last 10 runs of my DAG",
        icon: <BarChart2 size={14} color="#438593" />,
        category: "visualization"
      },
      {
        id: 8,
        text: "Show me the logs for my DAG run",
        icon: <FileText size={14} color="#438593" />,
        category: "logs"
      }
    ],

    // Pool 5: Data Transformation & Git Workflows
    [
      {
        id: 13,
        text: "Show me my current DBT project structure",
        icon: <FileText size={14} color="#438593" />,
        category: "project_structure"
      },
      {
        id: 14,
        text: "Help me improve my DBT model",
        icon: <Database size={14} color="#438593" />,
        category: "data_transformation"
      },
    ]
  ];

  // CRM-specific question pools
  const crmQuestionPools = [
    [
      {
        id: 'crm-1',
        text: "Which customers haven't ordered in over a week?",
        icon: <AlertCircle size={14} color="#438593" />,
        category: "customers"
      },
      {
        id: 'crm-2',
        text: "What inventory is aging that I should push?",
        icon: <Database size={14} color="#438593" />,
        category: "inventory"
      },
    ],
    [
      {
        id: 'crm-3',
        text: "Show me my open tasks for today",
        icon: <CheckCircle size={14} color="#438593" />,
        category: "tasks"
      },
      {
        id: 'crm-4',
        text: "What's the weather forecast for my delivery areas?",
        icon: <Activity size={14} color="#438593" />,
        category: "weather"
      },
    ],
    [
      {
        id: 'crm-5',
        text: "Which customers should I prioritize contacting?",
        icon: <AlertCircle size={14} color="#438593" />,
        category: "customers"
      },
      {
        id: 'crm-6',
        text: "What products are trending with my top customers?",
        icon: <BarChart2 size={14} color="#438593" />,
        category: "analysis"
      },
    ],
    [
      {
        id: 'crm-7',
        text: "Draft a follow-up email for my cold customers",
        icon: <FileText size={14} color="#438593" />,
        category: "outreach"
      },
      {
        id: 'crm-8',
        text: "Summarize my recent communications with customers",
        icon: <Clock size={14} color="#438593" />,
        category: "communications"
      },
    ]
  ];

  const activeQuestionPools = mode === 'crm' ? crmQuestionPools : questionPools;

  // Reset currentSet when mode changes
  useEffect(() => {
    setCurrentSet(0);
  }, [mode]);

  // Rotate question pools every 5 seconds with smooth transitions
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      
      // After fade out, change the set and fade in
      setTimeout(() => {
        setCurrentSet((prev) => (prev + 1) % activeQuestionPools.length);
        setIsTransitioning(false);
      }, 300); // Half of transition duration
    }, 8000);

    return () => clearInterval(interval);
  }, [activeQuestionPools.length]);

  // Don't show suggested questions if there are already messages beyond the initial greeting
  if (!isVisible || messages.length > 1) {
    return null;
  }

  const questions = activeQuestionPools[currentSet];

  const handleQuestionClick = (question) => {
    if (onQuestionClick) {
      onQuestionClick(question.text);
    }
  };

  return (
    <div 
      className="suggested-questions-container"
      style={{
        padding: '15px',
        borderTop: isDarkMode ? '1.2px solid #3c3c3c' : '1.2px solid #dfe0eb',
        backgroundColor: isDarkMode ? '#252526' : '#fafbfc',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}
    >
      <div
        style={{
          fontSize: '11px',
          fontWeight: '500',
          color: isDarkMode ? '#cccccc' : '#737d85',
          marginBottom: '4px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <span>Suggested Questions</span>
        <div style={{ 
          display: 'flex', 
          gap: '3px',
          alignItems: 'center'
        }}>
          {activeQuestionPools.map((_, index) => (
            <div
              key={index}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: index === currentSet ? '#438593' : '#cbd5e1',
                transition: 'all 0.3s ease'
              }}
            />
          ))}
        </div>
      </div>
      <div
        style={{
          fontSize: '9px',
          color: isDarkMode ? '#858585' : '#9ca3af',
          marginBottom: '8px',
          fontStyle: 'italic'
        }}
      >
        {mode === 'crm'
          ? "Ask about customers, inventory, tasks, and more"
          : "More questions will be added as we expand our AI tool capabilities"
        }
      </div>
      <div 
        className="suggested-questions-grid"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'translateY(10px)' : 'translateY(0)',
          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {questions.map((question) => (
          <button
            key={question.id}
            onClick={() => handleQuestionClick(question)}
            className="suggested-question-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              backgroundColor: isDarkMode ? '#3c3c3c' : '#ffffff',
              border: isDarkMode ? '1px solid #3c3c3c' : '1px solid #e2e8f0',
              borderRadius: '6px',
              color: isDarkMode ? '#ffffff' : '#334155',
              fontSize: '11px',
              fontWeight: '400',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textAlign: 'left',
              width: '100%',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              fontFamily: 'var(--bs-body-font-family)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8fafc';
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(1px)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '4px',
                backgroundColor: isDarkMode ? '#252526' : '#f1f5f9',
                flexShrink: 0
              }}
            >
              {question.icon}
            </div>
            <span style={{ flexGrow: 1 }}>
              {question.text}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SuggestedQuestions;